import type { ElementKind } from "../types";

export type BorderStyle = "solid" | "dashed" | "dotted";
export type BorderAlign = "inside" | "center" | "outside";

export interface NormalizedBorder {
  enabled: boolean;
  color?: string;
  width: number;
  opacity: number; // 0..1
  style: BorderStyle;
  align: BorderAlign;
}

function isBorderStyle(v: unknown): v is BorderStyle {
  return v === "solid" || v === "dashed" || v === "dotted";
}

function isBorderAlign(v: unknown): v is BorderAlign {
  return v === "inside" || v === "center" || v === "outside";
}

export function borderDashForStyle(style: unknown, borderWidth: number): number[] | undefined {
  const w = Math.max(0, Number(borderWidth) || 0);
  if (style === "dashed") return [Math.max(6, w * 4), Math.max(4, w * 3)];
  if (style === "dotted") return [Math.max(1, w), Math.max(3, w * 2)];
  return undefined;
}

/**
 * Normalizes Phase 3 `border*` fields, with fallback to legacy `stroke*` fields.
 * Keeps `enabled` false if there's no usable color/width.
 */
export function normalizeBorder(el: any): NormalizedBorder {
  const hasAnyBorderField =
    "borderEnabled" in (el ?? {}) ||
    "borderColor" in (el ?? {}) ||
    "borderWidth" in (el ?? {}) ||
    "borderOpacity" in (el ?? {}) ||
    "borderStyle" in (el ?? {}) ||
    "borderAlign" in (el ?? {});

  const rawEnabled = hasAnyBorderField ? !!el?.borderEnabled : undefined;
  const rawColor = (hasAnyBorderField ? el?.borderColor : undefined) ?? el?.stroke;
  const rawWidth = hasAnyBorderField ? el?.borderWidth : el?.strokeWidth;
  const rawOpacity = hasAnyBorderField ? el?.borderOpacity : el?.strokeOpacity;
  const rawStyle = hasAnyBorderField ? el?.borderStyle : el?.strokeStyle;
  const rawAlign = el?.borderAlign;

  const width = Math.max(0, Number(rawWidth) || 0);
  const opacity = Math.max(0, Math.min(1, Number(rawOpacity ?? 1)));
  const style: BorderStyle = isBorderStyle(rawStyle) ? rawStyle : "solid";
  const align: BorderAlign = isBorderAlign(rawAlign) ? rawAlign : "center";

  const enabled = rawEnabled ?? (width > 0.001 && !!rawColor);
  return { enabled: enabled && width > 0.001 && !!rawColor, color: rawColor, width, opacity, style, align };
}

export function supportsBorderForKind(kind: ElementKind): boolean {
  return (
    kind === "rect" ||
    kind === "circle" ||
    kind === "line" ||
    kind === "polygon" ||
    kind === "star" ||
    kind === "arrow" ||
    kind === "image" ||
    kind === "video"
  );
}

export function supportsCurveForKind(kind: ElementKind): boolean {
  return kind === "rect" || kind === "image" || kind === "video";
}

